// src/features/auth/authSlice.js (Final Version)

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  signIn,
  signOut,
  userLoaded,
  forgetPassword,
  resetPassword,
  update,
} from "./authAxios";

// Helper function for consistent error message handling
const getErrorMessage = (error) => {
  return (
    (error.response && error.response.data && error.response.data.message) ||
    error.message ||
    error.toString()
  );
};

// Kept original thunk name: login
export const login = createAsyncThunk("auth/login", async (data, thunkAPI) => {
  try {
    return await signIn(data);
  } catch (error) {
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

// Kept original thunk name: userLoad
// CORRECTED: No longer needs thunkAPI to get the token.
export const UserLoad = createAsyncThunk(
  "auth/user-loaded",
  async (_, thunkAPI) => {
    try {
      return await userLoaded();
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// Kept original thunk name: logout
// CORRECTED: No longer needs thunkAPI to get the token.
export const logout = createAsyncThunk("auth/logout", async (_, thunkAPI) => {
  try {
    return await signOut();
  } catch (error) {
    console.error("Server logout failed, but logging out locally.", error);
    return; // Resolve to ensure the fulfilled reducer still runs.
  }
});

// Kept original thunk name: Forget_Password
export const Forget_Password = createAsyncThunk(
  "auth/forget-password",
  async (data, thunkAPI) => {
    try {
      return await forgetPassword(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// Kept original thunk name: Reset_Password
export const Reset_Password = createAsyncThunk(
  "auth/reset-password",
  async (data, thunkAPI) => {
    try {
      return await resetPassword(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// Kept original thunk name: Update
// CORRECTED: No longer needs thunkAPI to get the token.
export const Update = createAsyncThunk(
  "auth/update",
  async (data, thunkAPI) => {
    try {
      return await update(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// The rest of the slice remains the same, as the extraReducers logic is already correct.
const initialState = {
  isLoading: false,
  isAuth: !!localStorage.getItem("token"),
  isError: false,
  isSuccess: false,
  message: "",
  userLoaded: false,
  user: {},
  destination: null,
  destinations: [],
  token: localStorage.getItem("token") || null,

  role: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setDestination: (state, { payload }) => {
      state.destination = payload; // Load all destinations (e.g. from an API)
    },
    setDestinations: (state, { payload }) => {
      state.destinations = payload; // Load all destinations (e.g. from an API)
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.data?.token) {
          state.isAuth = true;
          state.isSuccess = true;
          state.user = action.payload.data.user;
          state.token = action.payload.data.token;
          localStorage.setItem("token", action.payload.data.token);
          toast.success(action.payload.message);
        } else {
          state.isError = true;
          state.message = action.payload.message;
          toast.error(action.payload.message);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isAuth = false;
        state.message = action.payload;
        state.user = {};
        state.token = null;
        localStorage.removeItem("token");
        toast.error(action.payload);
      })

      // userloaded
      .addCase(UserLoad.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(UserLoad.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuth = true;
        state.user = action.payload.data;
        state.userLoaded = true;
      })
      .addCase(UserLoad.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuth = false;
        state.userLoaded = true;
        state.user = {};
        state.token = null;
        localStorage.removeItem("token");
      })

      // logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuth = false;
        state.user = {};
        state.token = null;
        state.role = null;
        state.userLoaded = true;
        localStorage.removeItem("token");
        toast.success(action.payload?.message || "Successfully logged out");
      })

      // Forget Password
      .addCase(Forget_Password.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(Forget_Password.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        toast.success(action.payload?.message);
      })
      .addCase(Forget_Password.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        toast.error(action.payload);
      })

      // Reset Password
      .addCase(Reset_Password.fulfilled, (state, action) => {
        toast.success(action.payload?.message);
      })
      .addCase(Reset_Password.rejected, (state, action) => {
        toast.error(action.payload);
      })

      // update
      .addCase(Update.pending, (state) => {
        // state.isLoading = true;
      })
      .addCase(Update.fulfilled, (state, action) => {
        // state.isLoading = false;
        state.isSuccess = true;
        // state.user = action.payload.data;
        toast.success(action.payload?.message);
      })
      .addCase(Update.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        toast.error(action.payload);
      });
  },
});

export default authSlice.reducer;
export const { setDestination, setDestinations } = authSlice.actions;
